'use client';

import { useTranslation } from 'react-i18next';
import {
  ShoppingCart,
  CreditCard,
  CheckSquare,
  CheckCircle2,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';

export interface MarketplaceCheckoutStepperProps {
  currentStep?: number;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEFAULT_ICONS: LucideIcon[] = [CheckSquare, ShoppingCart, CreditCard, CheckCircle2];

/** Checkout progress indicator — akademi-lms-v2's cart `Stepper.js`
 *  (Cart → Payment → Confirmation → Complete). Presentational only: which
 *  step is "current" is an authored prop, not derived from real checkout
 *  state, see {@link MarketplaceCatalogGrid} for why this block family has
 *  no live checkout backend yet. */
export function MarketplaceCheckoutStepper({
  currentStep = 1,
  paddingOuter,
  paddingInner,
}: MarketplaceCheckoutStepperProps) {
  const { t } = useTranslation('site-builder-blocks');
  const list = [
    t('marketplace.stepper.chooseTraining'),
    t('marketplace.stepper.createOrder'),
    t('marketplace.stepper.completePayment'),
    t('marketplace.stepper.registered'),
  ];

  const GREEN = 'var(--color-success-500, #22c55e)';
  const BRAND = 'var(--color-brand-500, #319db3)';
  const MUTED_BG = 'var(--color-surface-muted, #f1f5f9)';
  const MUTED_LINE = 'var(--color-border-default, rgba(0,0,0,0.14))';

  return (
    <SectionShell paddingOuter={paddingOuter} paddingInner={paddingInner}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
          overflowX: 'auto',
        }}
      >
        {list.map((label, i) => {
          const stepNumber = i + 1;
          const done = stepNumber < currentStep;
          const active = stepNumber === currentStep;
          const Icon = DEFAULT_ICONS[i % DEFAULT_ICONS.length] ?? CheckCircle2;
          return (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                flex: i < list.length - 1 ? 1 : '0 0 auto',
                minWidth: i < list.length - 1 ? 90 : 'auto',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: done ? GREEN : active ? BRAND : MUTED_BG,
                    color:
                      done || active
                        ? 'var(--color-text-on-brand, #fff)'
                        : 'var(--color-text-tertiary, #94a3b8)',
                    boxShadow: active
                      ? `0 0 0 4px color-mix(in srgb, ${BRAND} 18%, transparent)`
                      : 'none',
                    transition: 'background 200ms ease',
                  }}
                >
                  {done ? <Check size={20} strokeWidth={3} /> : <Icon size={18} />}
                </div>
                <span
                  style={{
                    fontSize: 'var(--font-size-xs, 12px)',
                    color:
                      active || done
                        ? 'var(--color-text-primary, #18181b)'
                        : 'var(--color-text-tertiary, #94a3b8)',
                    fontWeight: active ? 700 : 500,
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {label}
                </span>
              </div>
              {i < list.length - 1 && (
                <div
                  aria-hidden
                  style={{
                    flex: 1,
                    height: 3,
                    borderRadius: 999,
                    marginBottom: 24,
                    marginInline: 4,
                    background: done ? GREEN : MUTED_LINE,
                    transition: 'background 200ms ease',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}
